import { isEmpty } from 'ramda';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { withStyles } from '@material-ui/core/styles';
import DirectionsWalk from '@material-ui/icons/DirectionsWalk';
import Help from '@material-ui/icons/Help';
import TrendingUp from '@material-ui/icons/TrendingUp';
import LineStyle from '@material-ui/icons/LineStyle';
import Timeline from '@material-ui/icons/Timeline';
import ChromeReaderMode from '@material-ui/icons/ChromeReaderMode';
import Settings from '@material-ui/icons/Settings';
import { Switch, Redirect } from 'react-router-dom';

import { FEATURE_FLAG_NAMES } from 'yeself-common/typings/FeatureFlags';

import { selectFeatureFlags } from 'yeself-client-common/featureFlags/FeatureFlagsSelector';

import PrivateRoute from 'yeself-client-common/security/components/PrivateRoute';

import EnvironmentConfiguration from 'yeself-client-common/EnvironmentConfiguration';

import { getApplicationSettingsData } from 'Console/data/applicationDetail/settings/SettingsAction';

import {
    selectIsApplicationSettingsLoading,
    selectIsContentLanguageInitialized,
    selectApplicationSystem,
} from 'Console/data/applicationDetail/settings/SettingsSelector';

import DetailWrapper from 'Console/components/common/detailWrapper/DetailWrapper';
import ApplicationGuides from 'Console/components/applications/detail/guides/GuidesContainer';
import ApplicationContextHelp from 'Console/components/applications/detail/contextHelp/ContextHelp';
import ApplicationJourneys from 'Console/components/applications/detail/journeys/Journeys';
import ApplicationSegments from 'Console/components/applications/detail/segments/Segments';
import ApplicationThemes from 'Console/components/applications/detail/themes/ThemesContainer';
import ApplicationSettings from 'Console/components/applications/detail/settings/Settings';

import { unmountApplicationDetail } from 'Console/data/applications/actions';
import { resetApplicationDetail } from 'Console/data/applicationDetail/actions';
import { ROUTES } from 'Console/constants/routes';

import ContentCloudIcon from './icons/ContentCloudIcon';
import SplitScreenIcon from './icons/SplitScreenIcon';
import SmartPageIcon from './icons/SmartPageIcon';
import TooltipIcon from './icons/TooltipIcon';

const styles = () => ({
    icon: {
        'margin-right': 10,
        'font-size': 24,
        height: 16,
    },
    iconPng: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        margin: '0 5px',
    },
});

const finarfinNavLink = (applicationId) =>
    `${EnvironmentConfiguration.ANALYTICS_URL}/app/advanced-analytics/?applicationId=${applicationId}`;

const adopt10ContentCloudNavLink = (systemSlug) =>
    `${EnvironmentConfiguration.ADOPT_1_0_ADMIN_URL}/${systemSlug}/content-cloud`;

const adopt10SplitScreenNavLink = (systemSlug) =>
    `${EnvironmentConfiguration.ADOPT_1_0_ADMIN_URL}/${systemSlug}/split-screen-builder`;

const adopt10SmartPagesNavLink = (systemSlug) =>
    `${EnvironmentConfiguration.ADOPT_1_0_ADMIN_URL}/${systemSlug}/smart-pages`;

const adopt10TooltipsNavLink = (systemSlug) =>
    `${EnvironmentConfiguration.ADOPT_1_0_ADMIN_URL}/${systemSlug}/tooltips`;

const analyticsNavLink = (applicationId) => finarfinNavLink(applicationId);

class ApplicationDetail extends Component {
    componentDidMount() {
        const { match, getApplicationSettingsData } = this.props;
        getApplicationSettingsData(match.params.appId);
    }

    componentWillUnmount() {
        const { unmountApplicationDetail, resetApplicationDetail } = this.props;

        resetApplicationDetail();
        unmountApplicationDetail();
    }

    navLinks = (url) => {
        const { intl, classes, match, featureFlags, system } = this.props;

        const navLinks = [];

        if (featureFlags[FEATURE_FLAG_NAMES.GUIDES]) {
            navLinks.push({
                id: 'guides',
                displayName: intl.formatMessage({ id: 'Applications.navigation.detail.guides' }),
                navLinkTo: `${url}${ROUTES.GUIDES}`,
                component: ApplicationGuides,
                icon: <DirectionsWalk className={classes.icon} />,
            });
        }

        if (featureFlags[FEATURE_FLAG_NAMES.CONTEXT_HELP]) {
            navLinks.push({
                id: 'context-help',
                displayName: intl.formatMessage({
                    id: 'Applications.navigation.detail.contextHelp',
                }),
                navLinkTo: `${url}${ROUTES.CONTEXT_HELP}`,
                component: ApplicationContextHelp,
                icon: <Help className={classes.icon} />,
            });
        }

        if (featureFlags[FEATURE_FLAG_NAMES.JOURNEYS]) {
            navLinks.push({
                id: 'journeys',
                displayName: intl.formatMessage({ id: 'Applications.navigation.detail.journeys' }),
                navLinkTo: `${url}${ROUTES.JOURNEYS}`,
                component: ApplicationJourneys,
                icon: <TrendingUp className={classes.icon} />,
            });
        }

        if (featureFlags[FEATURE_FLAG_NAMES.SEGMENTS]) {
            navLinks.push({
                id: 'segments',
                displayName: intl.formatMessage({ id: 'Applications.navigation.detail.segments' }),
                navLinkTo: `${url}${ROUTES.SEGMENTS}`,
                component: ApplicationSegments,
                icon: <LineStyle className={classes.icon} />,
            });
        }

        if (featureFlags[FEATURE_FLAG_NAMES.THEME_BUILDER]) {
            navLinks.push({
                id: 'themes',
                displayName: intl.formatMessage({ id: 'Applications.navigation.detail.themes' }),
                navLinkTo: `${url}${ROUTES.THEMES}`,
                component: ApplicationThemes,
                icon: <ChromeReaderMode className={classes.icon} />,
            });
        }

        if (featureFlags[FEATURE_FLAG_NAMES.FINARFIN_ANALYTICS_CONSOLE]) {
            navLinks.push({
                id: 'analytics',
                displayName: intl.formatMessage({ id: 'Applications.navigation.detail.analytics' }),
                navLinkTo: analyticsNavLink(match.params.appId),
                icon: <Timeline className={classes.icon} />,
                isRedirect: true,
            });
        }

        const isSystemSlugSetup = !isEmpty(system.slug);

        if (featureFlags[FEATURE_FLAG_NAMES.ADOPT_1_0_CONTENT_CLOUD] && isSystemSlugSetup) {
            navLinks.push({
                id: 'adopt10ContentCloud',
                displayName: intl.formatMessage({
                    id: 'Applications.navigation.detail.adopt10.contentCloud',
                }),
                navLinkTo: adopt10ContentCloudNavLink(system.slug),
                isRedirect: true,
                icon: <ContentCloudIcon className={classes.iconPng} />,
            });
        }

        if (featureFlags[FEATURE_FLAG_NAMES.ADOPT_1_0_SPLIT_SCREEN] && isSystemSlugSetup) {
            navLinks.push({
                id: 'adopt10SplitScreen',
                displayName: intl.formatMessage({
                    id: 'Applications.navigation.detail.adopt10.splitScreen',
                }),
                navLinkTo: adopt10SplitScreenNavLink(system.slug),
                isRedirect: true,
                icon: <SplitScreenIcon className={classes.iconPng} />,
            });
        }

        if (featureFlags[FEATURE_FLAG_NAMES.ADOPT_1_0_SMART_PAGES] && isSystemSlugSetup) {
            navLinks.push({
                id: 'adopt10SmartPages',
                displayName: intl.formatMessage({
                    id: 'Applications.navigation.detail.adopt10.smartPages',
                }),
                navLinkTo: adopt10SmartPagesNavLink(system.slug),
                isRedirect: true,
                icon: <SmartPageIcon className={classes.iconPng} />,
            });
        }

        if (featureFlags[FEATURE_FLAG_NAMES.ADOPT_1_0_TOOLTIPS] && isSystemSlugSetup) {
            navLinks.push({
                id: 'adopt10Tooltips',
                displayName: intl.formatMessage({
                    id: 'Applications.navigation.detail.adopt10.tooltips',
                }),
                navLinkTo: adopt10TooltipsNavLink(system.slug),
                isRedirect: true,
                icon: <TooltipIcon className={classes.iconPng} />,
            });
        }

        if (featureFlags[FEATURE_FLAG_NAMES.SETTINGS]) {
            navLinks.push({
                id: 'settings',
                displayName: intl.formatMessage({ id: 'Applications.navigation.detail.settings' }),
                navLinkTo: `${url}${ROUTES.SETTINGS}`,
                component: ApplicationSettings,
                icon: <Settings className={classes.icon} />,
            });
        }

        return navLinks;
    };

    render() {
        const { match, isLoading, isContentLanguageInitialized } = this.props;

        if (isLoading || !isContentLanguageInitialized) {
            return null;
        }

        return (
            <DetailWrapper
                matchUrl={match.url}
                params={match.params}
                navLinks={this.navLinks(match.url)}
            >
                <Switch>
                    {this.navLinks(match.path)
                        .filter((item) => !item.isRedirect)
                        .map((route) => (
                            <PrivateRoute
                                key={`route-${route.navLinkTo}`}
                                path={route.navLinkTo}
                                params={match.params}
                                component={route.component}
                            />
                        ))}
                    <Redirect key="redirect" from={match.url} to={`${match.url}${ROUTES.GUIDES}`} />
                </Switch>
            </DetailWrapper>
        );
    }
}

export default withStyles(styles)(
    injectIntl(
        connect(
            (state) => ({
                isLoading: selectIsApplicationSettingsLoading(state),
                isContentLanguageInitialized: selectIsContentLanguageInitialized(state),
                featureFlags: selectFeatureFlags(state),
                system: selectApplicationSystem(state),
            }),
            {
                getApplicationSettingsData,
                unmountApplicationDetail,
                resetApplicationDetail,
            }
        )(ApplicationDetail)
    )
);
